import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';
import { buildHumanoidGroup } from './FpsCharacterModel.js';

// ─── Exported Types ──────────────────────────────────────────────────────────

export type BossKind = 'shadow_striker' | 'stone_giant' | 'storm_witch';

export type KeycardDrop = {
  kind: BossKind;
  pos: THREE.Vector3;
  label: string;
};

export interface BossHitResult {
  bossKind: BossKind;
  damage: number;
  killed: boolean;
  keycard: KeycardDrop | null;
}

export interface BossObjectiveState {
  kind: BossKind;
  name: string;
  x: number;
  z: number;
  alive: boolean;
}

// ─── Internal Types ──────────────────────────────────────────────────────────

interface BossProjectile {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  ttl: number;
}

interface BossState {
  kind: BossKind;
  name: string;
  hp: number;
  maxHp: number;
  spawn: THREE.Vector3;
  group: THREE.Group;
  bodyMeshes: THREE.Mesh[];
  color: number;
  patrolAngle: number;
  fireCd: number;
  alive: boolean;
  dying: boolean;
  dyingT: number;
  projectiles: BossProjectile[];
  keycardDrop: KeycardDropState | null;
  particles: GoldParticle[];
}

interface KeycardDropState {
  mesh: THREE.Mesh;
  keycard: KeycardDrop;
  pulse: number;
}

interface GoldParticle {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  ttl: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const BOSS_DEFS: Array<{
  kind: BossKind;
  name: string;
  spawn: [number, number, number];
  color: number;
  hue: number;
  keycardLabel: string;
}> = [
  {
    kind: 'shadow_striker',
    name: 'Shadow Striker',
    spawn: [2, 1, -38],
    color: 0x2a0a3a,
    hue: 0.78,
    keycardLabel: 'Mythic AR',
  },
  {
    kind: 'stone_giant',
    name: 'Stone Giant',
    spawn: [32, 1, -32],
    color: 0x7a6a55,
    hue: 0.08,
    keycardLabel: 'Vault Keycard Alpha',
  },
  {
    kind: 'storm_witch',
    name: 'Storm Witch',
    spawn: [-22, 1, -28],
    color: 0x0abfbf,
    hue: 0.5,
    keycardLabel: 'Vault Keycard Beta',
  },
];

const BOSS_HP = 500;
const PATROL_RADIUS = 10;
const PATROL_SPEED = 0.4; // rad/s
const AGGRO_RANGE = 30;
const BOSS_BAR_RANGE = 40;
const FIRE_INTERVAL = 2.0;
const PROJ_SPEED = 18;
const PROJ_DAMAGE = 35;
const PROJ_TTL = 3.0;
const PROJ_RADIUS = 0.22;
const DEATH_SCALE_TIME = 0.5;
const KEYCARD_COLLECT_RANGE = 2.0;
const BOSS_SCALE = 2.0;

// ─── Class ────────────────────────────────────────────────────────────────────

export class FpsBossFights {
  private bosses: BossState[] = [];
  private toonGrad: THREE.DataTexture;
  private projMats: Record<BossKind, THREE.MeshToonMaterial>;
  private keycardMat: THREE.MeshToonMaterial;
  private goldMat: THREE.MeshStandardMaterial;

  // DOM
  private bossBarEl: HTMLDivElement;
  private bossBarFill: HTMLDivElement;
  private bossBarName: HTMLSpanElement;

  constructor(
    private scene: THREE.Scene,
    private mount: HTMLElement,
  ) {
    this.toonGrad = createToonGradientMap();

    // Projectile materials per boss
    this.projMats = {
      shadow_striker: toonMat(0x9933ff, this.toonGrad, { emissive: 0x6600cc, emissiveIntensity: 0.6 }),
      stone_giant: toonMat(0x8b7355, this.toonGrad, { emissive: 0x5c4a2a, emissiveIntensity: 0.3 }),
      storm_witch: toonMat(0x00ffff, this.toonGrad, { emissive: 0x00cccc, emissiveIntensity: 0.8 }),
    };

    // Keycard drop material
    this.keycardMat = toonMat(0xffd700, this.toonGrad, { emissive: 0xffaa00, emissiveIntensity: 1.2 });

    // Gold particle material
    this.goldMat = new THREE.MeshStandardMaterial({ color: 0xffd700, emissive: 0xffa500, emissiveIntensity: 1.5 });

    // Build DOM boss bar
    this.bossBarEl = document.createElement('div');
    this.bossBarEl.id = 'fps-boss-bar';
    Object.assign(this.bossBarEl.style, {
      position: 'fixed',
      top: '18px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '340px',
      background: 'rgba(10,5,15,0.82)',
      border: '2px solid rgba(255,200,0,0.55)',
      borderRadius: '8px',
      padding: '8px 14px',
      zIndex: '5000',
      display: 'none',
      pointerEvents: 'none',
      userSelect: 'none',
      fontFamily: 'monospace',
    });

    this.bossBarName = document.createElement('span');
    Object.assign(this.bossBarName.style, {
      color: '#ffe033',
      fontSize: '13px',
      fontWeight: 'bold',
      display: 'block',
      marginBottom: '5px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    this.bossBarEl.appendChild(this.bossBarName);

    const barBg = document.createElement('div');
    Object.assign(barBg.style, {
      width: '100%',
      height: '12px',
      background: '#2a0a0a',
      borderRadius: '4px',
      overflow: 'hidden',
    });
    this.bossBarFill = document.createElement('div');
    Object.assign(this.bossBarFill.style, {
      height: '100%',
      background: 'linear-gradient(90deg, #cc0000, #ff4444)',
      borderRadius: '4px',
      transition: 'width 0.1s',
    });
    barBg.appendChild(this.bossBarFill);
    this.bossBarEl.appendChild(barBg);

    this.mount.appendChild(this.bossBarEl);

    // Spawn bosses
    for (const def of BOSS_DEFS) {
      this._spawnBoss(def);
    }
  }

  // ── Spawn ──────────────────────────────────────────────────────────────────

  private _spawnBoss(def: (typeof BOSS_DEFS)[number]): void {
    const group = buildHumanoidGroup(def.hue, this.toonGrad);
    group.scale.setScalar(BOSS_SCALE);

    const spawnVec = new THREE.Vector3(...def.spawn);
    group.position.copy(spawnVec);
    group.userData.bossKind = def.kind;

    this.scene.add(group);

    // Collect all meshes inside group for hit detection
    const bodyMeshes: THREE.Mesh[] = [];
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.userData.bossKind = def.kind;
        bodyMeshes.push(child);
      }
    });

    this.bosses.push({
      kind: def.kind,
      name: def.name,
      hp: BOSS_HP,
      maxHp: BOSS_HP,
      spawn: spawnVec.clone(),
      group,
      bodyMeshes,
      color: def.color,
      patrolAngle: Math.random() * Math.PI * 2,
      fireCd: FIRE_INTERVAL * Math.random(),
      alive: true,
      dying: false,
      dyingT: 0,
      projectiles: [],
      keycardDrop: null,
      particles: [],
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Returns hit result if a boss projectile hit player this tick. */
  update(dt: number, playerPos: THREE.Vector3, _playerDamagePerHit: number): BossHitResult | null {
    let hitResult: BossHitResult | null = null;

    // Find nearest living boss for the HP bar
    let nearestBoss: BossState | null = null;
    let nearestDist = Infinity;

    for (const boss of this.bosses) {
      if (!boss.alive) {
        // Tick dying animation
        if (boss.dying) {
          boss.dyingT += dt;
          const t = Math.min(boss.dyingT / DEATH_SCALE_TIME, 1);
          const s = BOSS_SCALE * (1 - t);
          boss.group.scale.setScalar(Math.max(s, 0));
          if (boss.dyingT >= DEATH_SCALE_TIME) {
            boss.dying = false;
            this.scene.remove(boss.group);
          }
        }

        // Tick gold particles
        this._tickParticles(boss, dt);

        // Tick keycard pulse
        if (boss.keycardDrop) {
          boss.keycardDrop.pulse += dt * 3;
          boss.keycardDrop.mesh.position.y =
            boss.keycardDrop.keycard.pos.y + 0.5 + Math.sin(boss.keycardDrop.pulse) * 0.15;
          boss.keycardDrop.mesh.rotation.y += dt * 1.2;
        }
        continue;
      }

      const distToPlayer = boss.group.position.distanceTo(playerPos);

      if (distToPlayer < nearestDist) {
        nearestDist = distToPlayer;
        nearestBoss = boss;
      }

      // ── Patrol ──────────────────────────────────────────────────────────
      if (distToPlayer > AGGRO_RANGE) {
        boss.patrolAngle += PATROL_SPEED * dt;
        boss.group.position.x = boss.spawn.x + Math.cos(boss.patrolAngle) * PATROL_RADIUS;
        boss.group.position.z = boss.spawn.z + Math.sin(boss.patrolAngle) * PATROL_RADIUS;
      } else {
        // ── Aggro: face player ──────────────────────────────────────────
        const dir = new THREE.Vector3().subVectors(playerPos, boss.group.position);
        dir.y = 0;
        if (dir.lengthSq() > 0.001) {
          boss.group.rotation.y = Math.atan2(dir.x, dir.z);
        }

        // ── Fire ─────────────────────────────────────────────────────────
        boss.fireCd -= dt;
        if (boss.fireCd <= 0) {
          boss.fireCd = FIRE_INTERVAL;
          this._fireProjectile(boss, playerPos);
        }
      }

      // ── Tick projectiles ─────────────────────────────────────────────────
      const projHit = this._tickProjectiles(boss, dt, playerPos);
      if (projHit && !hitResult) hitResult = projHit;
    }

    // ── Boss HP bar ──────────────────────────────────────────────────────────
    if (nearestBoss && nearestDist <= BOSS_BAR_RANGE) {
      this.bossBarEl.style.display = 'block';
      this.bossBarName.textContent = `☠ ${nearestBoss.name}  ${nearestBoss.hp} / ${nearestBoss.maxHp}`;
      const pct = Math.max(0, nearestBoss.hp / nearestBoss.maxHp) * 100;
      this.bossBarFill.style.width = `${pct}%`;
    } else {
      this.bossBarEl.style.display = 'none';
    }

    return hitResult;
  }

  /** Call when the weapon system raycasts and hits something — pass the hit point + damage. */
  tryPlayerShot(point: THREE.Vector3, damage: number): BossHitResult | null {
    for (const boss of this.bosses) {
      if (!boss.alive) continue;
      // Check if point is within boss bounding volume (~2m radius at 2x scale)
      const dist = boss.group.position.distanceTo(point);
      if (dist > 2.2) continue;

      boss.hp -= damage;
      const killed = boss.hp <= 0;
      let keycard: KeycardDrop | null = null;

      if (killed) {
        boss.hp = 0;
        keycard = this._killBoss(boss);
      }

      return { bossKind: boss.kind, damage, killed, keycard };
    }
    return null;
  }

  /** Returns all boss body meshes for weapon system raycasting. */
  getBossHitMeshes(): THREE.Object3D[] {
    const meshes: THREE.Object3D[] = [];
    for (const boss of this.bosses) {
      if (boss.alive) meshes.push(...boss.bodyMeshes);
    }
    return meshes;
  }

  getObjectiveStates(): BossObjectiveState[] {
    return this.bosses.map((boss) => ({
      kind: boss.kind,
      name: boss.name,
      x: boss.group.position.x,
      z: boss.group.position.z,
      alive: boss.alive,
    }));
  }

  /** Returns keycard if player walks within 2m of a dropped keycard. */
  collectKeycard(playerPos: THREE.Vector3): KeycardDrop | null {
    for (const boss of this.bosses) {
      if (!boss.keycardDrop) continue;
      const dist = boss.keycardDrop.keycard.pos.distanceTo(playerPos);
      if (dist <= KEYCARD_COLLECT_RANGE) {
        const kc = boss.keycardDrop.keycard;
        this.scene.remove(boss.keycardDrop.mesh);
        boss.keycardDrop = null;
        return kc;
      }
    }
    return null;
  }

  dispose(): void {
    for (const boss of this.bosses) {
      this.scene.remove(boss.group);
      for (const p of boss.projectiles) this.scene.remove(p.mesh);
      for (const g of boss.particles) this.scene.remove(g.mesh);
      if (boss.keycardDrop) this.scene.remove(boss.keycardDrop.mesh);
    }
    this.bosses = [];
    if (this.bossBarEl.parentElement) this.bossBarEl.parentElement.removeChild(this.bossBarEl);
  }

  // ── Private Helpers ────────────────────────────────────────────────────────

  private _fireProjectile(boss: BossState, target: THREE.Vector3): void {
    const geo = new THREE.SphereGeometry(PROJ_RADIUS, 8, 6);
    const mesh = new THREE.Mesh(geo, this.projMats[boss.kind]);
    mesh.castShadow = false;

    // Fire from boss chest height
    const origin = boss.group.position.clone();
    origin.y += 2.4; // chest height at 2x scale
    mesh.position.copy(origin);

    const dir = new THREE.Vector3().subVectors(target, origin).normalize();
    const velocity = dir.multiplyScalar(PROJ_SPEED);

    this.scene.add(mesh);
    boss.projectiles.push({ mesh, velocity, ttl: PROJ_TTL });
  }

  private _tickProjectiles(boss: BossState, dt: number, playerPos: THREE.Vector3): BossHitResult | null {
    let result: BossHitResult | null = null;
    const toRemove: BossProjectile[] = [];

    for (const proj of boss.projectiles) {
      proj.ttl -= dt;
      proj.mesh.position.addScaledVector(proj.velocity, dt);

      // Check player hit (within ~0.7m)
      if (proj.mesh.position.distanceTo(playerPos) < 0.7) {
        result = { bossKind: boss.kind, damage: PROJ_DAMAGE, killed: false, keycard: null };
        toRemove.push(proj);
        continue;
      }

      if (proj.ttl <= 0) toRemove.push(proj);
    }

    for (const p of toRemove) {
      this.scene.remove(p.mesh);
      boss.projectiles.splice(boss.projectiles.indexOf(p), 1);
    }

    return result;
  }

  private _killBoss(boss: BossState): KeycardDrop {
    boss.alive = false;
    boss.dying = true;
    boss.dyingT = 0;

    // Remove all in-flight projectiles
    for (const p of boss.projectiles) this.scene.remove(p.mesh);
    boss.projectiles = [];

    const deathPos = boss.group.position.clone();

    // Spawn 8 gold particles
    for (let i = 0; i < 8; i++) {
      const geo = new THREE.SphereGeometry(0.12, 6, 4);
      const mesh = new THREE.Mesh(geo, this.goldMat);
      mesh.position.copy(deathPos).add(new THREE.Vector3(0, 1.5, 0));
      const angle = (i / 8) * Math.PI * 2;
      const vel = new THREE.Vector3(
        Math.cos(angle) * 4 + (Math.random() - 0.5) * 2,
        4 + Math.random() * 3,
        Math.sin(angle) * 4 + (Math.random() - 0.5) * 2,
      );
      this.scene.add(mesh);
      boss.particles.push({ mesh, vel, ttl: 1.2 });
    }

    // Spawn keycard chest cube
    const kcPos = deathPos.clone();
    kcPos.y = 1.0;
    const kcGeo = new THREE.BoxGeometry(0.6, 0.6, 0.6);
    const kcMesh = new THREE.Mesh(kcGeo, this.keycardMat);
    kcMesh.position.copy(kcPos);
    this.scene.add(kcMesh);

    const keycard: KeycardDrop = {
      kind: boss.kind,
      pos: kcPos.clone(),
      label: BOSS_DEFS.find((d) => d.kind === boss.kind)!.keycardLabel,
    };

    boss.keycardDrop = { mesh: kcMesh, keycard, pulse: 0 };

    return keycard;
  }

  private _tickParticles(boss: BossState, dt: number): void {
    const toRemove: GoldParticle[] = [];
    for (const p of boss.particles) {
      p.ttl -= dt;
      p.vel.y -= 9.8 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      const s = Math.max(0, p.ttl / 1.2);
      p.mesh.scale.setScalar(s);
      if (p.ttl <= 0) {
        this.scene.remove(p.mesh);
        toRemove.push(p);
      }
    }
    for (const p of toRemove) boss.particles.splice(boss.particles.indexOf(p), 1);
  }
}
